/**
 * qty-spinner.js — Product Card Quantity Spinner
 * Branch: qty-spinner
 *
 * Safe / stable mode:
 *  - No optimistic UI. UI only updates after the API responds.
 *  - All buttons are blocked (pointer-events: none) while a request is in flight.
 *  - Loading ring shows on the add button; pill dims while changing qty.
 *  - One request at a time per variant — predictable, no race conditions.
 */
(function () {
  'use strict';

  var QtySpinner = {

    cart: null,
    _busy: {}, // { vid: true } — tracks in-flight requests per variant

    init: function () {
      var self = this;
      this.fetchCart(function (cart) {
        self.cart = cart;
        self.syncAllCards();
        self.syncMultiCards();
      });

      // Single delegated listener
      document.addEventListener('click', function (e) {
        // ---- Multi-variant packaging/variant selector (pill or footer "+") ----
        var pillBtn  = e.target.closest('[data-qty-pill]');
        var multiBtn = e.target.closest('[data-qty-multi-trigger]');
        if (pillBtn || multiBtn) {
          e.preventDefault(); e.stopPropagation();
          VariantSelector.toggle(pillBtn || multiBtn);
          return;
        }

        var selectorCloseBtn = e.target.closest('[data-qty-close]');
        if (selectorCloseBtn) {
          e.preventDefault(); e.stopPropagation();
          VariantSelector.close({ restoreFocus: true });
          return;
        }

        // ---- Quantity controls: single-variant card, selector rows, and
        // mini-cart all reuse the exact same .product-card-qty__add/__plus
        // /__minus markup, so one generic handler covers all of them —
        // el.closest('.product-card-qty') always resolves to whichever
        // instance (card, or the specific selector row) was actually clicked.
        var addBtn    = e.target.closest('.product-card-qty__add');
        var plusBtn   = e.target.closest('.product-card-qty__plus');
        var minusBtn  = e.target.closest('.product-card-qty__minus');
        var miniPlus   = e.target.closest('.mini-cart-qty__plus');
        var miniMinus  = e.target.closest('.mini-cart-qty__minus');
        var miniRemove = e.target.closest('.mini-cart-remove');

        if (addBtn)     { e.preventDefault(); e.stopPropagation(); self.onAdd(addBtn.closest('.product-card-qty')); }
        if (plusBtn)    { e.preventDefault(); e.stopPropagation(); self.onPlus(plusBtn.closest('.product-card-qty')); }
        if (minusBtn)   { e.preventDefault(); e.stopPropagation(); self.onMinus(minusBtn.closest('.product-card-qty')); }
        if (miniPlus)   { e.preventDefault(); e.stopPropagation(); self.miniOnPlus(miniPlus.closest('.mini-cart-qty')); }
        if (miniMinus)  { e.preventDefault(); e.stopPropagation(); self.miniOnMinus(miniMinus.closest('.mini-cart-qty')); }
        if (miniRemove && !miniRemove.disabled) { e.preventDefault(); e.stopPropagation(); self.miniOnRemove(miniRemove); }
      });

      // Stop carousel (Swiper/Owl) from swallowing mousedown/touchstart —
      // covers the single-variant spinner plus the new pill / footer trigger.
      function blockCarousel(e) {
        if (e.target.closest('.product-card-qty, .qty-interactive')) e.stopPropagation();
      }
      document.addEventListener('mousedown',  blockCarousel, true);
      document.addEventListener('touchstart', blockCarousel, { capture: true, passive: true });
    },

    /* ---- Cart helpers ---- */

    fetchCart: function (cb) {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(cb)
        .catch(function () { cb({ items: [], item_count: 0, total_price: 0 }); });
    },

    cartQty: function (variantId) {
      if (!this.cart || !this.cart.items) return 0;
      for (var i = 0; i < this.cart.items.length; i++) {
        if (String(this.cart.items[i].variant_id) === String(variantId)) {
          return this.cart.items[i].quantity;
        }
      }
      return 0;
    },

    /* ---- UI helpers ---- */

    syncAllCards: function () {
      var self = this;
      document.querySelectorAll('.product-card-qty[data-variant-id]').forEach(function (el) {
        self.setUI(el, self.cartQty(el.dataset.variantId));
      });
    },

    /* Multi-variant cards can't show one combined spinner (ambiguous which
       variant), so instead every card for a given product shows a running
       total — summed across all of that product's variants — permanently
       integrated into the same red trigger control (never a separate
       badge element). Refreshed on every cart update, including other
       cards for the same product rendered elsewhere on the page. */
    syncMultiCards: function () {
      var self = this;
      document.querySelectorAll('.product-card-qty__variants-data').forEach(function (script) {
        var cardRoot = script.closest('.single-product-wrap');
        if (!cardRoot) return;
        var trigger = cardRoot.querySelector('[data-qty-multi-trigger]');
        if (!trigger) return;

        var variants = [];
        try { variants = JSON.parse(script.textContent) || []; } catch (err) { variants = []; }

        var total = 0;
        variants.forEach(function (v) { total += self.cartQty(v.id); });

        var countEl = trigger.querySelector('.product-card-qty__add-count');
        if (countEl) countEl.textContent = total > 99 ? '99+' : String(total);
        trigger.classList.toggle('product-card-qty__add--counted', total > 0);
      });
    },

    /* The selector shares this shared error slot for row-level add/plus/
       minus failures; single-variant cards have no error UI, so these are
       no-ops outside an open selector. */
    notifySelectorError: function (el) {
      if (el.closest('.qty-variant-selector') && typeof VariantSelector !== 'undefined') {
        VariantSelector.showError();
      }
    },

    clearSelectorError: function (el) {
      if (el.closest('.qty-variant-selector') && typeof VariantSelector !== 'undefined') {
        VariantSelector.hideError();
      }
    },

    setUI: function (el, qty) {
      var valueEl = el.querySelector('.product-card-qty__value');
      if (valueEl) valueEl.textContent = qty;
      el.classList.toggle('product-card-qty--active', qty > 0);
    },

    /* Lock / unlock all cards for a variant while request is in flight */
    lock: function (vid) {
      this._busy[vid] = true;
      document.querySelectorAll('.product-card-qty[data-variant-id="' + vid + '"]').forEach(function (card) {
        card.classList.add('product-card-qty--loading');
      });
    },

    unlock: function (vid) {
      delete this._busy[vid];
      document.querySelectorAll('.product-card-qty[data-variant-id="' + vid + '"]').forEach(function (card) {
        card.classList.remove('product-card-qty--loading');
      });
    },

    isBusy: function (vid) {
      return !!this._busy[vid];
    },

    /* Called after every successful, server-confirmed API response. This is
       the ONLY place displayed quantities are written — never optimistically
       from the click itself — and the ONLY place the cache (this.cart) is
       replaced. Until this runs, the previous cart is stale-but-still-shown;
       no UI updates happen in between. */
    afterChange: function (cart, vid) {
      this.cart = cart;
      var self = this;
      document.querySelectorAll('.product-card-qty[data-variant-id="' + vid + '"]').forEach(function (card) {
        self.setUI(card, self.cartQty(vid));
      });
      this.unlock(vid);
      this.syncMultiCards();
      if (typeof Shopify !== 'undefined' && typeof Shopify.onCartUpdate === 'function') {
        Shopify.onCartUpdate(cart);
      }
    },

    /* ---- Product card action handlers ---- */
    /* Shared by collection-card spinners, mini-cart, and every row inside
       the multi-variant selector — see buildVariantRow() in the selector
       section below. Quantity is only ever read from/written to the
       confirmed cart; nothing here guesses at what the server will accept. */

    onAdd: function (el) {
      var vid = el.dataset.variantId;
      if (this.isBusy(vid)) return;
      var self = this;
      this.lock(vid);
      this.clearSelectorError(el);

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: vid, quantity: 1 })
      })
      .then(function (r) {
        if (!r.ok) { return r.json().catch(function () { return {}; }).then(function () { throw new Error('add_failed'); }); }
        return r.json();
      })
      .then(function () {
        // /cart/add.js returns only the line item; fetch full cart for totals
        self.fetchCart(function (cart) {
          self.afterChange(cart, vid);
        });
      })
      .catch(function () {
        self.unlock(vid);
        self.notifySelectorError(el);
      });
    },

    onPlus: function (el) {
      var vid        = el.dataset.variantId;
      if (this.isBusy(vid)) return;
      var currentQty = this.cartQty(vid);
      var self       = this;
      this.lock(vid);
      this.clearSelectorError(el);

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: vid, quantity: currentQty + 1 })
      })
      .then(function (r) {
        if (!r.ok) { throw new Error('change_failed'); }
        return r.json();
      })
      .then(function (cart) { self.afterChange(cart, vid); })
      .catch(function () {
        self.unlock(vid);
        self.notifySelectorError(el);
      });
    },

    onMinus: function (el) {
      var vid        = el.dataset.variantId;
      if (this.isBusy(vid)) return;
      var currentQty = this.cartQty(vid);
      var newQty     = Math.max(0, currentQty - 1);
      var self       = this;
      this.lock(vid);
      this.clearSelectorError(el);

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: vid, quantity: newQty })
      })
      .then(function (r) {
        if (!r.ok) { throw new Error('change_failed'); }
        return r.json();
      })
      .then(function (cart) { self.afterChange(cart, vid); })
      .catch(function () {
        self.unlock(vid);
        self.notifySelectorError(el);
      });
    },

    /* ---- Mini-cart qty controls ---- */
    /* Increment, decrement AND remove all funnel through this one
       server-confirmed mutation: lock -> /cart/change.js -> afterChange()
       -> Shopify.onCartUpdate(). Quantity 0 is a normal, fully supported
       value here — the server drops the line entirely, and the mini-cart
       HTML that afterChange()/onCartUpdate() regenerates simply won't
       include it any more. There is no local "hide this row" step. */

    miniLock: function (vid) {
      this._busy[vid] = true;
      document.querySelectorAll('.mini-cart-qty[data-variant-id="' + vid + '"]').forEach(function (el) {
        el.classList.add('mini-cart-qty--loading');
      });
      document.querySelectorAll('.mini-cart-remove[data-variant-id="' + vid + '"]').forEach(function (el) {
        el.disabled = true;
      });
    },

    miniUnlock: function (vid) {
      delete this._busy[vid];
      document.querySelectorAll('.mini-cart-qty[data-variant-id="' + vid + '"]').forEach(function (el) {
        el.classList.remove('mini-cart-qty--loading');
      });
      document.querySelectorAll('.mini-cart-remove[data-variant-id="' + vid + '"]').forEach(function (el) {
        el.disabled = false;
      });
    },

    showMiniCartError: function () {
      var el = document.querySelector('[data-mini-cart-error]');
      if (!el) return;
      var i18n = (typeof VariantSelector !== 'undefined') ? VariantSelector.i18n() : (window.QtySpinnerI18n || {});
      el.textContent = i18n.cartUpdateError || 'Не удалось обновить корзину. Попробуйте ещё раз.';
      el.hidden = false;
    },

    hideMiniCartError: function () {
      var el = document.querySelector('[data-mini-cart-error]');
      if (el) el.hidden = true;
    },

    /* newQty === 0 is how both "minus from 1" and the trash button remove
       a line — same request, same confirmed-cart update path either way. */
    miniChangeQty: function (el, newQty) {
      var vid = el.dataset.variantId;
      if (this.isBusy(vid)) return;
      var self = this;
      this.hideMiniCartError();
      this.miniLock(vid);

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: vid, quantity: newQty })
      })
      .then(function (r) {
        if (!r.ok) { throw new Error('change_failed'); }
        return r.json();
      })
      .then(function (cart) {
        // afterChange() calls unlock(vid), and Shopify.onCartUpdate()
        // rebuilds .cart-item-loop from the confirmed cart — on success
        // there is nothing left to "unlock" in the old DOM, it's replaced.
        self.afterChange(cart, vid);
      })
      .catch(function () {
        // Keep the row exactly as it was — no optimistic removal/quantity
        // change ever happened — just release the controls and surface
        // the existing Russian cart error message.
        self.unlock(vid);
        self.miniUnlock(vid);
        self.showMiniCartError();
      });
    },

    miniOnPlus: function (el) {
      this.miniChangeQty(el, this.cartQty(el.dataset.variantId) + 1);
    },

    miniOnMinus: function (el) {
      this.miniChangeQty(el, Math.max(0, this.cartQty(el.dataset.variantId) - 1));
    },

    /* btnEl is the trash button itself; it carries the same
       data-variant-id as its sibling .mini-cart-qty, so miniChangeQty()
       works with either element. */
    miniOnRemove: function (btnEl) {
      this.miniChangeQty(btnEl, 0);
    }

  };

  /* =====================================================================
     Multi-variant packaging / variant selector (popover on desktop,
     bottom sheet on mobile <= 767px). One reusable element appended to
     <body>, repositioned + repopulated per card. Reuses QtySpinner's
     fetchCart()/afterChange() so cart state, .bigcounter and the mini-cart
     stay in sync through the exact same code path as the spinner.
     ===================================================================== */

  /**
   * Supported title conventions used to identify single-unit vs. box
   * variants for the "Выгоднее на N%" savings message. This is intentionally
   * conservative — if the title doesn't match a recognised pattern, no
   * saving is calculated (never guess).
   *
   *  Single-unit examples:  "1 саше — 20 г", "Саше — 20 г", "1 пакетик — 20 г"
   *  Box examples:          "Коробка — 20 × 20 г", "Коробка — 10 x 20 г",
   *                         "Коробка 20 шт", "Коробка — 20 саше",
   *                         "Упаковка — 20 × 20 г"
   *  Supported multiplier separators: × x X х Х · *
   *  (only recognised when digits appear on both sides, e.g. "20 × 20 г",
   *  so a stray "*" or "·" elsewhere in a title is never treated as one).
   */
  function parseBoxCount(title) {
    if (!title) return null;
    var t = String(title).toLowerCase();

    // "<N> <sep> <N>" — e.g. 20 × 20 г / 20 x 20 г / 20 · 20 г / 20 * 20 г
    var multiplierMatch = t.match(/(\d+)\s*[×x\u0445·*]\s*\d+/);
    if (multiplierMatch) {
      var n = parseInt(multiplierMatch[1], 10);
      if (n > 1) return n;
    }

    // Box/pack keyword + "<N> шт|саше|пакетик" — e.g. "Коробка 20 шт"
    if (/коробка|упаковка/.test(t)) {
      var keywordMatch = t.match(/(\d+)\s*(шт|саше|пакетик)/);
      if (keywordMatch) {
        var n2 = parseInt(keywordMatch[1], 10);
        if (n2 > 1) return n2;
      }
    }

    return null;
  }

  function isSingleUnitTitle(title) {
    if (!title) return false;
    if (parseBoxCount(title) !== null) return false;
    var t = String(title).toLowerCase();
    return /(^|[\s\-—])(1\s*)?(саше|пакетик)\b/.test(t);
  }

  /**
   * Returns a map of variantId -> integer saving percent for box variants
   * that are confidently cheaper than buying the identified single-unit
   * variant that many times over. Only returns an entry when both formats
   * were identified confidently, prices are valid, and the box is genuinely
   * cheaper (percent > 0). Uses integer cent prices throughout.
   */
  function computeSavings(variants) {
    var savings = {};
    var singleCandidates = (variants || []).filter(function (v) {
      return isSingleUnitTitle(v.title) && v.price > 0;
    });
    if (!singleCandidates.length) return savings;

    var single = singleCandidates.reduce(function (min, v) {
      return v.price < min.price ? v : min;
    }, singleCandidates[0]);

    (variants || []).forEach(function (v) {
      if (v.id === single.id || !(v.price > 0)) return;
      var count = parseBoxCount(v.title);
      if (!count) return;
      var singleTotal = single.price * count;
      if (v.price >= singleTotal) return;
      var pct = Math.floor(((singleTotal - v.price) / singleTotal) * 100);
      if (pct > 0) savings[v.id] = pct;
    });

    return savings;
  }

  function formatPriceHtml(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatAedCartMoney === 'function') {
      return Shopify.formatAedCartMoney(cents);
    }
    return 'AED ' + (cents / 100).toFixed(2);
  }

  /**
   * Builds one selector row. Quantity state is always rendered from the
   * currently confirmed cart (currentQty), never assumed — so reopening the
   * selector, or a mini-cart change elsewhere, is always reflected correctly.
   * Available rows get the exact same .product-card-qty / __add / __spinner
   * markup as the collection card, so QtySpinner.onAdd/onPlus/onMinus,
   * lock()/unlock() and setUI() all work on them completely unmodified —
   * that's what lets a row flip from "+" to a live "− N +" spinner using
   * the existing, already-tested, server-confirmed code path.
   */
  function buildVariantRow(v, savingPct, i18n, currentQty) {
    var li = document.createElement('li');
    li.className = 'qty-variant-selector__row' + (v.available ? '' : ' is-unavailable');

    var img = document.createElement('img');
    img.className = 'qty-variant-selector__row-img';
    img.src = v.image || '';
    img.alt = '';
    img.loading = 'lazy';
    li.appendChild(img);

    var info = document.createElement('div');
    info.className = 'qty-variant-selector__row-info';

    var titleEl = document.createElement('p');
    titleEl.className = 'qty-variant-selector__row-title';
    titleEl.textContent = v.title;
    info.appendChild(titleEl);

    if (v.available) {
      var priceEl = document.createElement('p');
      priceEl.className = 'qty-variant-selector__row-price';
      var priceHtml = formatPriceHtml(v.price);
      if (v.compareAtPrice > v.price) {
        priceHtml += ' <span class="old-price">' + formatPriceHtml(v.compareAtPrice) + '</span>';
      }
      priceEl.innerHTML = priceHtml;
      info.appendChild(priceEl);

      if (savingPct) {
        var savingEl = document.createElement('p');
        savingEl.className = 'qty-variant-selector__row-saving';
        savingEl.textContent = (i18n.savingTemplate || 'Выгоднее на {percent}%').replace('{percent}', savingPct);
        info.appendChild(savingEl);
      }
    } else {
      var unavailEl = document.createElement('p');
      unavailEl.className = 'qty-variant-selector__row-unavailable';
      unavailEl.textContent = i18n.unavailable || 'Нет в наличии';
      info.appendChild(unavailEl);
    }

    li.appendChild(info);

    if (!v.available) {
      var disabledBtn = document.createElement('button');
      disabledBtn.type = 'button';
      disabledBtn.className = 'product-card-qty__add';
      disabledBtn.disabled = true;
      disabledBtn.setAttribute('aria-label', (i18n.unavailable || 'Нет в наличии') + ': ' + v.title);
      disabledBtn.textContent = '+';
      li.appendChild(disabledBtn);
      return li;
    }

    var qty = currentQty > 0 ? currentQty : 0;
    var controls = document.createElement('div');
    controls.className = 'product-card-qty qty-variant-selector__row-qty' + (qty > 0 ? ' product-card-qty--active' : '');
    controls.setAttribute('data-variant-id', v.id);

    var addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'product-card-qty__add';
    addBtn.setAttribute('aria-label', v.title);
    addBtn.textContent = '+';
    controls.appendChild(addBtn);

    var spinner = document.createElement('div');
    spinner.className = 'product-card-qty__spinner';

    var minusBtn = document.createElement('button');
    minusBtn.type = 'button';
    minusBtn.className = 'product-card-qty__minus';
    minusBtn.setAttribute('aria-label', '\u2212 ' + v.title);
    minusBtn.innerHTML = '&#8722;';
    spinner.appendChild(minusBtn);

    var valueEl = document.createElement('span');
    valueEl.className = 'product-card-qty__value';
    valueEl.textContent = qty;
    spinner.appendChild(valueEl);

    var plusBtn = document.createElement('button');
    plusBtn.type = 'button';
    plusBtn.className = 'product-card-qty__plus';
    plusBtn.setAttribute('aria-label', '+ ' + v.title);
    plusBtn.textContent = '+';
    spinner.appendChild(plusBtn);

    controls.appendChild(spinner);
    li.appendChild(controls);

    return li;
  }

  var VariantSelector = {
    el: null,
    overlayEl: null,
    panelEl: null,
    titleEl: null,
    errorEl: null,
    rowsEl: null,
    closeEl: null,
    mode: null,          // 'popover' | 'sheet'
    trigger: null,
    cardRoot: null,

    i18n: function () {
      return window.QtySpinnerI18n || {};
    },

    isMobile: function () {
      return window.matchMedia('(max-width: 767px)').matches;
    },

    ensureBuilt: function () {
      if (this.el) return;
      var i18n = this.i18n();
      var el = document.createElement('div');
      el.className = 'qty-variant-selector';
      el.id = 'qty-variant-selector';
      el.hidden = true;
      el.innerHTML =
        '<div class="qty-variant-selector__overlay" data-qty-overlay></div>' +
        '<div class="qty-variant-selector__panel" role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="qty-variant-selector-title">' +
          '<div class="qty-variant-selector__header">' +
            '<p class="qty-variant-selector__title" id="qty-variant-selector-title"></p>' +
            '<button type="button" class="qty-variant-selector__close" data-qty-close aria-label="' + (i18n.close || 'Закрыть') + '">&times;</button>' +
          '</div>' +
          '<div class="qty-variant-selector__error" data-qty-error></div>' +
          '<ul class="qty-variant-selector__rows" data-qty-rows></ul>' +
        '</div>';
      document.body.appendChild(el);

      this.el         = el;
      this.overlayEl  = el.querySelector('[data-qty-overlay]');
      this.panelEl    = el.querySelector('.qty-variant-selector__panel');
      this.titleEl    = el.querySelector('.qty-variant-selector__title');
      this.errorEl    = el.querySelector('[data-qty-error]');
      this.rowsEl     = el.querySelector('[data-qty-rows]');
      this.closeEl    = el.querySelector('[data-qty-close]');
    },

    isOpen: function () {
      return !!(this.el && this.el.classList.contains('is-open'));
    },

    /* Pill and footer "+" both open the same selector for a given card */
    toggle: function (triggerEl) {
      var cardRoot = triggerEl.closest('.single-product-wrap');
      if (this.isOpen() && this.cardRoot === cardRoot) {
        this.close({ restoreFocus: true });
        return;
      }
      this.open(triggerEl);
    },

    open: function (triggerEl) {
      this.ensureBuilt();
      var cardRoot = triggerEl.closest('.single-product-wrap');
      if (!cardRoot) return;

      // Only one selector open at a time — reset the previous trigger first.
      if (this.isOpen()) {
        this.setTriggerState(this.cardRoot, false);
        this.unbindOpenListeners();
      }

      var dataScript = cardRoot.querySelector('.product-card-qty__variants-data');
      var variants = [];
      if (dataScript) {
        try { variants = JSON.parse(dataScript.textContent) || []; } catch (err) { variants = []; }
      }
      if (!variants.length) return;

      this.trigger  = triggerEl;
      this.cardRoot = cardRoot;
      this.hideError();
      this.populate(variants, dataScript && dataScript.dataset.selectorTitle);

      var mobile = this.isMobile();
      this.mode = mobile ? 'sheet' : 'popover';
      this.el.classList.remove('qty-variant-selector--popover', 'qty-variant-selector--sheet');
      this.el.classList.add(mobile ? 'qty-variant-selector--sheet' : 'qty-variant-selector--popover');
      this.el.hidden = false;
      this.el.classList.add('is-open');
      this.el.style.top = '';
      this.el.style.left = '';

      if (mobile) {
        document.body.classList.add('qty-variant-sheet-open');
      } else {
        this.position(triggerEl);
      }

      this.setTriggerState(cardRoot, true);
      this.bindOpenListeners();

      var self = this;
      window.setTimeout(function () {
        if (self.closeEl) self.closeEl.focus();
      }, 0);
    },

    /* titleText is the fully-resolved, already-translated phrase rendered
       server-side into data-selector-title (see product-grid-item.liquid),
       chosen from the product's actual Shopify option name - "Выберите
       вес", "Выберите объём", etc. i18n.chooseVariant is only a defensive
       fallback for the (normally unreachable) case where that attribute is
       missing - not a hard-coded phrase, it's read from locales/*.json via
       window.QtySpinnerI18n. There is no subtitle; the title is the only
       heading text in the selector. */
    populate: function (variants, titleText) {
      var i18n = this.i18n();
      this.titleEl.textContent = titleText || i18n.chooseVariant || '';

      var savings = computeSavings(variants);
      this.rowsEl.innerHTML = '';
      var self = this;
      variants.forEach(function (v) {
        // Always read the confirmed cart, never assume — this is what makes
        // reopening the selector (or a change made elsewhere, e.g. the
        // mini-cart) always show correct, server-confirmed state. Rows for
        // available variants reuse .product-card-qty[data-variant-id], so
        // QtySpinner.syncAllCards()/afterChange() keep them in sync for
        // free on every subsequent cart update — no separate refresh path
        // needed here.
        var currentQty = QtySpinner.cartQty(v.id);
        self.rowsEl.appendChild(buildVariantRow(v, savings[v.id], i18n, currentQty));
      });
    },

    position: function (triggerEl) {
      var panel = this.panelEl;
      var el = this.el;
      var margin = 10;

      var triggerRect = triggerEl.getBoundingClientRect();
      var panelRect = panel.getBoundingClientRect();

      var top = triggerRect.bottom + 8;
      var left = triggerRect.left;

      if (top + panelRect.height > window.innerHeight - margin) {
        var above = triggerRect.top - panelRect.height - 8;
        top = above < margin ? margin : above;
      }
      if (left + panelRect.width > window.innerWidth - margin) {
        left = window.innerWidth - margin - panelRect.width;
      }
      if (left < margin) left = margin;
      if (top < margin) top = margin;

      el.style.top = top + 'px';
      el.style.left = left + 'px';
    },

    setTriggerState: function (cardRoot, isOpen) {
      if (!cardRoot || !cardRoot.isConnected) return;
      var trigger = cardRoot.querySelector('[data-qty-multi-trigger]');
      var pill    = cardRoot.querySelector('[data-qty-pill]');

      if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        // Icon lives in its own inner span so toggling it never clobbers
        // the sibling cart-count badge (see QtySpinner.syncMultiCards()).
        var icon = trigger.querySelector('.product-card-qty__add-icon');
        if (icon) icon.textContent = isOpen ? '\u2212' : '+';
        if (isOpen) trigger.setAttribute('aria-controls', 'qty-variant-selector');
        else trigger.removeAttribute('aria-controls');
      }
      if (pill) {
        pill.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) pill.setAttribute('aria-controls', 'qty-variant-selector');
        else pill.removeAttribute('aria-controls');
      }
    },

    close: function (opts) {
      opts = opts || {};
      if (!this.el || !this.isOpen()) return;

      var cardRoot = this.cardRoot;
      var trigger  = this.trigger;

      this.el.classList.remove('is-open');
      this.el.hidden = true;
      document.body.classList.remove('qty-variant-sheet-open');
      this.unbindOpenListeners();
      this.setTriggerState(cardRoot, false);

      this.trigger  = null;
      this.cardRoot = null;
      this.mode     = null;

      if (opts.restoreFocus && trigger && trigger.isConnected) {
        trigger.focus();
      }
    },

    bindOpenListeners: function () {
      var self = this;

      this._onKeydown = function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          self.close({ restoreFocus: true });
          return;
        }
        if (e.key === 'Tab' && self.panelEl) {
          var focusables = Array.prototype.filter.call(
            self.panelEl.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
            function (node) { return !node.disabled && node.offsetParent !== null; }
          );
          if (!focusables.length) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      this._onDocClick = function (e) {
        if (!self.el) return;
        var insideSelector = self.el.contains(e.target);
        var onTrigger = self.trigger && (e.target === self.trigger || self.trigger.contains(e.target));
        if (!insideSelector && !onTrigger) self.close({ restoreFocus: false });
      };
      this._onResize = function () { self.close({ restoreFocus: false }); };
      this._onScroll = function () {
        if (self.mode === 'popover') self.close({ restoreFocus: false });
      };
      this._onOverlayClick = function () { self.close({ restoreFocus: true }); };

      document.addEventListener('keydown', this._onKeydown, true);
      document.addEventListener('click', this._onDocClick, true);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onScroll, true);
      if (this.overlayEl) this.overlayEl.addEventListener('click', this._onOverlayClick);
    },

    unbindOpenListeners: function () {
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown, true);
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick, true);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll, true);
      if (this.overlayEl && this._onOverlayClick) this.overlayEl.removeEventListener('click', this._onOverlayClick);
    },

    showError: function () {
      if (!this.errorEl) return;
      this.errorEl.textContent = this.i18n().addError || 'Не удалось добавить товар. Попробуйте ещё раз.';
      this.errorEl.classList.add('is-visible');
    },

    hideError: function () {
      if (this.errorEl) this.errorEl.classList.remove('is-visible');
    }

    /* There is intentionally no handleAdd()/setRowsDisabled() here anymore.
       Row add/increment/decrement buttons are plain
       .product-card-qty__add/__plus/__minus elements, so they're picked up
       by QtySpinner's own delegated click handler and go through
       onAdd()/onPlus()/onMinus() exactly like a collection card — same
       per-variant lock()/unlock(), same server-confirmed setUI(), same
       error reporting. The selector deliberately never closes on a
       successful add/change; only close()/Escape/outside-click/close
       button/trigger-removed do that. */
  };

  /* Safe init */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { QtySpinner.init(); });
  } else {
    QtySpinner.init();
  }

  /* Keep spinners, selector rows and multi-variant badges in sync whenever
     the cart changes from ANY path — spinner, mini-cart, or a future/other
     integration that calls Shopify.onCartUpdate directly. This is the single
     point where the cached cart is considered "current" again. */
  if (typeof Shopify !== 'undefined') {
    var _origOnCartUpdate = Shopify.onCartUpdate;
    Shopify.onCartUpdate = function (cart) {
      if (typeof _origOnCartUpdate === 'function') _origOnCartUpdate(cart);
      QtySpinner.cart = cart;
      QtySpinner.syncAllCards();
      QtySpinner.syncMultiCards();
    };
  }

  /* Body scroll lock: prevent background scrolling when mini-cart is open.
     Watches for .mini-cart gaining/losing the .show class. */
  var miniCartEl = document.querySelector('.mini-cart');
  if (miniCartEl && typeof MutationObserver !== 'undefined') {
    new MutationObserver(function () {
      if (miniCartEl.classList.contains('show')) {
        document.body.classList.add('mini-cart-open');
      } else {
        document.body.classList.remove('mini-cart-open');
      }
    }).observe(miniCartEl, { attributes: true, attributeFilter: ['class'] });
  }

})();
